<template>
    <v-container fluid class="pt-0">
        <create-or-update-contract
            :is-open="isCreateOrUpdateDialogVisible"
            :contract="selected"
            @close="onCloseCreateOrUpdateDialog"
        />
        <confirm-delete
            :is-open="isConfirmDeleteDialogVisible"
            :selected="selected"
            @confirm="onConfirmDeleteDialog($event)"
            @close="onCloseConfirmDeleteDialog"
        />
        <confirm-restore
            :is-open="isConfirmRestoreDialogVisible"
            :selected="selected"
            @confirm="onConfirmRestoreDialog($event)"
            @close="onCloseConfirmRestoreDialog"
        />
        <entity-history
            :isVisible="isShowHistoryDialogVisible"
            :histories="histories"
            @close="onCloseShowHistoryDialog"
        >
            <template #title>
                Contract History
            </template>
        </entity-history>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <h1>{{ $t('contractsView.title') }}</h1>
                            </v-col>
                            <v-col v-if="isAdmin" cols="12">
                                <v-btn
                                    depressed
                                    medium
                                    class="mt-2"
                                    @click="onOpenCreateOrUpdateDialog(false)"
                                >
                                    {{ $t('contractsView.newContract') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-tabs v-model="currentRoleTab">
                                    <v-tab
                                        v-for="tabName in tabNames"
                                        :key="tabName"
                                        @click="updateSelectedTab(tabName)"
                                    >
                                        {{ tabName }}
                                    </v-tab>
                                </v-tabs>
                            </v-col>
                        </v-row>
                        <v-tabs-items v-model="currentRoleTab">
                            <v-tab-item
                                v-for="tabName in tabNames"
                                :key="tabName"
                            >
                                <contract-list
                                    :contracts="
                                        tabName === tabNames.active
                                            ? contracts
                                            : deletedContracts
                                    "
                                    :visibilityConfig="{
                                        actions: {
                                            edit: tabName === tabNames.active,
                                            delete: tabName === tabNames.active,
                                            showHistory: true,
                                            restore: tabName !== tabNames.active
                                        },
                                        search: true,
                                        refresh: true,
                                        excludeHeaders: isAdmin
                                            ? null
                                            : [
                                                  'user.firstName',
                                                  'user.lastName',
                                                  'action'
                                              ]
                                    }"
                                    @edit="onOpenCreateOrUpdateDialog"
                                    @delete="onDeleteItem"
                                    @refresh="onRefresh"
                                    @showHistory="onShowHistory"
                                    @restore="onRestoreItem"
                                />
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'contracts',
    components: {
        ContractList: () => import('@/components/ContractList'),
        CreateOrUpdateContract: () =>
            import('@/components/CreateOrUpdateContract'),
        ConfirmDelete: () => import('@/components/ConfirmDelete'),
        ConfirmRestore: () => import('@/components/ConfirmRestore'),
        EntityHistory: () => import('@/components/EntityHistory')
    },
    data() {
        return {
            isCreateOrUpdateDialogVisible: false,
            isConfirmDeleteDialogVisible: false,
            isShowHistoryDialogVisible: false,
            isConfirmRestoreDialogVisible: false,
            selected: null,
            histories: [],
            tabNames: {
                active: this.$t('contractsView.tabNames.activeContracts'),
                deleted: this.$t('contractsView.tabNames.deletedContracts')
            },
            currentRoleTab: null,
            currentTab: null
        };
    },
    computed: {
        ...mapGetters({
            contracts: 'contracts/getContracts',
            deletedContracts: 'contracts/getDeletedContracts',
            isAdmin: 'auth/isAdmin'
        })
    },
    async created() {
        await this.fetchContracts();
    },
    methods: {
        ...mapActions({
            fetchContracts: 'contracts/fetch',
            fetchDeletedContracts: 'contracts/fetchDeleted',
            restoreContract: 'contracts/restore',
            deleteContract: 'contracts/delete',
            fetchHistoriesByContractId: 'contracts/fetchHistoriesByEntityId'
        }),

        async fetchDeleted() {
            await this.fetchDeletedContracts();
        },
        onDeleteItem(item = {}) {
            this.selected = item;
            this.isConfirmDeleteDialogVisible = true;
        },
        onRestoreItem(item = {}) {
            this.selected = item;
            this.isConfirmRestoreDialogVisible = true;
        },
        async onConfirmRestoreDialog(item) {
            try {
                await this.restoreContract(item.id);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t('contractsView.contractTitle'),
                    text: this.$t('contractsView.restoreSuccess')
                });
            } catch (error) {
                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('contractsView.contractTitle'),
                    text: this.$t('contractsView.restoreError')
                });

                console.error(error);
            }

            this.onCloseConfirmRestoreDialog();
        },
        onCloseConfirmRestoreDialog() {
            this.selected = null;
            this.isConfirmRestoreDialogVisible = false;
        },
        async onConfirmDeleteDialog(item) {
            try {
                await this.deleteContract(item.id);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t('contractsView.contractTitle'),
                    text: this.$t('contractsView.deleteSuccess')
                });
            } catch (error) {
                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('contractsView.contractTitle'),
                    text: this.$t('contractsView.deleteError')
                });

                console.error(error);
            }

            this.onCloseConfirmDeleteDialog();
        },
        onCloseConfirmDeleteDialog() {
            this.selected = null;
            this.isConfirmDeleteDialogVisible = false;
        },
        onOpenCreateOrUpdateDialog(item = {}) {
            this.isCreateOrUpdateDialogVisible = true;

            if (item.id) {
                this.selected = item;
            }
        },
        onCloseCreateOrUpdateDialog() {
            this.selected = null;
            this.isCreateOrUpdateDialogVisible = false;
        },
        async onRefresh() {
            if (this.currentTab === this.tabNames.deleted) {
                await this.fetchDeletedContracts();
            } else {
                await this.fetchContracts();
            }
        },
        async onShowHistory({ id }) {
            try {
                this.histories = await this.fetchHistoriesByContractId(id);
            } catch (err) {
                const { response } = err;

                if (response && response.status === 404) {
                    return;
                }

                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('formCommonErrors.commonTitle'),
                    text: this.$t('formCommonErrors.serverError')
                });

                console.error(err);
            } finally {
                this.isShowHistoryDialogVisible = true;
            }
        },
        onCloseShowHistoryDialog() {
            this.histories = [];
            this.isShowHistoryDialogVisible = false;
        },
        async updateSelectedTab(tab) {
            this.currentTab = tab;

            if (
                !this.deletedContracts.length &&
                this.currentTab === this.tabNames.deleted
            ) {
                await this.fetchDeletedContracts();
            }
        }
    }
};
</script>
<style lang="scss"></style>
